import Swal from 'sweetalert2';
import { getFiltros } from './openFiltrosPedidoModal';
import { PrecheckPedido, PrecheckTransito, PrecheckResponse } from "@/types/sugerido.types";
import store from "@/store";
import type { AxiosInstance } from 'axios';
import makeRequest from "@/functions/http/makeRequest";

function setSinVmdOptionsHtml(data: any) {
    const includeSinVmd = data?.includeSinVmd === true;
    const cantidadSinVmd = Number.isFinite(data?.cantidadSinVmd) ? data.cantidadSinVmd : 1;

    return `
    <div class="p-3 rounded border border-gray-200 bg-gray-50">
        <div class="flex items-center gap-2">
            <input id="includeSinVmd" type="checkbox" class="mr-2" ${includeSinVmd ? 'checked' : ''}>
            <label for="includeSinVmd" class="text-sm font-semibold">
                Incluir productos sin VMD con vmd fija
            </label>
        </div>

        <div class="mt-2 flex items-center gap-2">
            <label for="cantidadSinVmd" class="text-sm font-bold text-neutral-700 dark:text-neutral-200">
                VMD fija:
            </label>
            <input
                type="number"
                min="1"
                step="1"
                id="cantidadSinVmd"
                value="${cantidadSinVmd}"
                ${includeSinVmd ? '' : 'disabled'}
                class="relative block w-32 min-w-0 flex-auto rounded border border-solid border-neutral-300 bg-white bg-clip-padding px-3 py-[0.25rem] text-base font-normal leading-[1.6] text-neutral-700 outline-none transition duration-200 ease-in-out disabled:bg-gray-100 disabled:text-gray-400"
            />
        </div>

        <div class="mt-2 text-xs text-gray-600">
            Se incluirán productos que no tengan VMD, usando la vmd fija indicada. ( VMD = 1 será equivalente a pedir 1 unidad de ese producto por dia establecido )
        </div>
    </div>
    `;
}

function setOptionsMarcaHtml(data: any) {
    let marcas = data.marcas;
    if (data?.checkboxStatesMarca?.length > 0) {
        marcas = data.checkboxStatesMarca;
    }

    let html = `
    <div class="flex items-center justify-between">
        <div class="flex items-center">
            <input type="checkbox" class="mr-2" value="selectAllOptions" id="selectAllOptionsMarca" checked>
            <h2 class="my-2 text-lg font-semibold mb-2">Selecciona las marcas:</h2>
        </div>
        <div class='pr-1'>
            <button title="Seleccionar todos" id="selectAllButtonMarca" class="text-blue-500 hover:text-blue-600 cursor-pointer transition duration-300 ease-in-out transform hover:scale-105">
                <i class='bx bx-list-check inline-block align-middle' style="font-size:30px;"></i>
            </button>
            <button title="Deseleccionar todos" id="deselectAllButtonMarca" class="text-red-500 hover:text-red-600 cursor-pointer transition duration-300 ease-in-out transform hover:scale-105">
                <i class='bx bx-list-minus inline-block align-middle' style="font-size:30px;"></i>
            </button>
        </div>
    </div>

    <div class="my-2 flex items-center border border-gray-300 rounded">
        <i class='bx bx-search text-gray-400 ml-2'></i>
        <input
            type="text"
            id="filterMarcasInput"
            placeholder="Filtrar marcas..."
            class="w-full py-1 px-2 outline-none"
        >
    </div>

    <div class="max-h-40 overflow-auto">
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-2" id="marcasContainer">`;

    marcas.map((item: any) => {
        html += `
            <label class="flex items-center text-left">
                <input type="checkbox" class="mr-2" value="${item?.marca ?? item?.name}" name="checkboxOptionMarca" ${item?.checked === true ? 'checked' : ''}>
                ${item?.marca ?? item?.name}
            </label>
        `;
    });

    html += `
        </div>
    </div>`;

    return html;
}

function setMaximo() {
    return `
    <div class="flex items-center">
        <label for="maximo" class="block text-base font-bold text-neutral-700 dark:text-neutral-200 mr-2">Establecer días: </label>
        <input type="text" id="maximo"
            class="relative block w-1/2 min-w-0 flex-auto rounded border border-solid border-neutral-300 bg-clip-padding px-3 py-[0.25rem] text-base font-normal leading-[1.6] text-neutral-700 outline-none transition duration-200 ease-in-out focus:z-[3] focus:border-primary focus:text-neutral-700 focus:shadow-[inset 0 0 0 1px rgba(59,113,202)] focus:outline-none dark:border-neutral-600 dark:text-neutral-200 dark:placeholder-text-neutral-200 dark:focus:border-primary"/>
    </div>
    `;
}

function setHtml(data: any) {
    return `
    <div class="grid gap-4 grid-cols-1">
        ${setMaximo()}
        ${setSinVmdOptionsHtml(data)}
        ${setOptionsMarcaHtml(data)}
    </div>
    `;
}

function getSelectedMarcas(payload: any): string[] {
    const list = Array.isArray(payload?.checkboxStatesMarca) ? payload.checkboxStatesMarca : [];
    return list
        .filter((x: any) => x?.checked === true)
        .map((x: any) => String(x?.name ?? '').trim())
        .filter((x: string) => x !== '');
}


function escapeHtml(value: any): string {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

function buildOverwriteWarningHtml(data: PrecheckResponse): string {
    const pedidos: PrecheckPedido[] = Array.isArray(data?.pedidos) ? data.pedidos : [];
    const marcas: string[] = Array.isArray(data?.marcas) ? data.marcas : [];
    const transitos: PrecheckTransito[] = Array.isArray(data?.transitos) ? data.transitos : [];

    const hasMarcas = data?.has_conflicts_marcas === true && pedidos.length > 0;
    const hasTransito = data?.has_conflicts_transito === true && transitos.length > 0;

    let html = `
        <div style="text-align:left; font-size:14px;">
            <p style="margin-bottom:12px;">
                Se detectaron conflictos antes de generar el sugerido. Revisa la información y confirma si deseas continuar.
            </p>
    `;

    if (hasMarcas) {
        html += `
            <div style="margin-bottom:16px; padding:12px; border:1px solid #f5c6cb; border-radius:8px; background:#fff5f5;">
                <div style="font-weight:700; color:#842029; margin-bottom:8px;">
                    Existen pedidos NUEVOS/ACTIVOS con estas marcas
                </div>
                <div style="margin-bottom:8px;">
                    <strong>Marcas:</strong> ${marcas.map((m: string) => escapeHtml(m)).join(', ')}
                </div>
                <div style="max-height:180px; overflow:auto; border:1px solid #eee; border-radius:6px; background:#fff;">
                    <table style="width:100%; border-collapse:collapse; font-size:13px;">
                        <thead style="background:#f8f9fa;">
                            <tr>
                                <th style="padding:8px; border-bottom:1px solid #eee; text-align:left;">Pedido</th>
                                <th style="padding:8px; border-bottom:1px solid #eee; text-align:left;">Fecha</th>
                                <th style="padding:8px; border-bottom:1px solid #eee; text-align:left;">Marca</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${pedidos.map((p: PrecheckPedido) => `
                                <tr>
                                    <td style="padding:8px; border-bottom:1px solid #f2f2f2;">${escapeHtml(p.id)}</td>
                                    <td style="padding:8px; border-bottom:1px solid #f2f2f2;">${escapeHtml(p.fecha)}</td>
                                    <td style="padding:8px; border-bottom:1px solid #f2f2f2;">${Array.isArray(p.marcas) ? p.marcas.map((m: string) => escapeHtml(m)).join(', ') : ''}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>

                <label style="display:flex; align-items:flex-start; gap:8px; margin-top:12px;">
                    <input type="checkbox" id="ack_overwrite_marcas" />
                    <span>Entiendo que existen pedidos activos con estas marcas y deseo continuar.</span>
                </label>
            </div>
        `;
    }

    if (hasTransito) {
        html += `
            <div style="margin-bottom:16px; padding:12px; border:1px solid #ffe69c; border-radius:8px; background:#fffdf2;">
                <div style="font-weight:700; color:#997404; margin-bottom:8px;">
                    Existen productos en tránsito
                </div>
                <div style="margin-bottom:8px;">
                    Marca los productos que ya llegaron para eliminarlos del tránsito antes de continuar.
                </div>
                <div style="max-height:260px; overflow:auto; border:1px solid #eee; border-radius:6px; background:#fff;">
                    <table style="width:100%; border-collapse:collapse; font-size:13px;">
                        <thead style="background:#f8f9fa;">
                            <tr>
                                <th style="padding:8px; border-bottom:1px solid #eee; text-align:center; width:40px;">
                                    <input type="checkbox" id="check_all_transitos" />
                                </th>
                                <th style="padding:8px; border-bottom:1px solid #eee; text-align:left;">Barra</th>
                                <th style="padding:8px;border-bottom:1px solid #eee;text-align:left;width: 350px;">Descripción</th>
                                <th style="padding:8px; border-bottom:1px solid #eee; text-align:left;">Cantidad</th>
                                <th style="padding:8px; border-bottom:1px solid #eee; text-align:left;">Proveedor</th>
                                <th style="padding:8px; border-bottom:1px solid #eee; text-align:left;">Fecha</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${transitos.map((t: PrecheckTransito) => `
                                <tr>
                                    <td style="padding:8px; border-bottom:1px solid #f2f2f2; text-align:center;">
                                        <input type="checkbox" class="check_transito_item" value="${escapeHtml(t.item)}" />
                                    </td>
                                    <td style="padding:8px; border-bottom:1px solid #f2f2f2;">${escapeHtml(t.barra)}</td>
                                    <td style="padding:8px; border-bottom:1px solid #f2f2f2;">${escapeHtml(t.desprod)}</td>
                                    <td style="padding:8px; border-bottom:1px solid #f2f2f2;">${escapeHtml(t.cantidad)}</td>
                                    <td style="padding:8px; border-bottom:1px solid #f2f2f2;">${escapeHtml(t.codprove)}</td>
                                    <td style="padding:8px; border-bottom:1px solid #f2f2f2;">${escapeHtml(t.fecha)}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>

                <label style="display:flex; align-items:flex-start; gap:8px; margin-top:12px;">
                    <input type="checkbox" id="ack_overwrite_transito" />
                    <span>Entiendo que los productos no marcados seguirán en tránsito y deseo continuar.</span>
                </label>
            </div>
        `;
    }

    html += `</div>`;

    return html;
}

export default function openSugeridoModal(axios: AxiosInstance, apiRoute: string) {
    Swal.showLoading();

    getFiltros(axios, apiRoute).then((response: any) => {
        const html = setHtml(response.data);

        Swal.fire({
            focusConfirm: false,
            title: 'Opciones de sugerido',
            html,
            showCancelButton: true,
            confirmButtonText: 'Generar',
            cancelButtonText: 'Cancelar',
            reverseButtons: true,
            customClass: {
                confirmButton: 'custom-confirm-button',
                popup: 'swal2-popup swal2-modal swal2-show swal-wide'
            },
            didOpen() {
                // --- SIN VMD: toggle enable/disable ---
                const includeSinVmdEl = document.getElementById('includeSinVmd') as HTMLInputElement | null;
                const cantidadSinVmdEl = document.getElementById('cantidadSinVmd') as HTMLInputElement | null;

                includeSinVmdEl?.addEventListener('change', () => {
                    const enabled = includeSinVmdEl.checked === true;
                    if (cantidadSinVmdEl) {
                        cantidadSinVmdEl.disabled = !enabled;
                        if (enabled && (!cantidadSinVmdEl.value || parseInt(cantidadSinVmdEl.value, 10) < 1)) {
                            cantidadSinVmdEl.value = '1';
                        }
                    }
                });

                // --- MARCAS ---
                const filterInputMarcas = document.getElementById('filterMarcasInput') as HTMLInputElement;
                const marcasContainer = document.getElementById('marcasContainer') as HTMLElement;

                const selectAllButtonMarca = document.getElementById('selectAllButtonMarca') as HTMLButtonElement;
                const deselectAllButtonMarca = document.getElementById('deselectAllButtonMarca') as HTMLButtonElement;
                const optionCheckboxesMarca = document.querySelectorAll('input[type="checkbox"][name="checkboxOptionMarca"]');

                selectAllButtonMarca.addEventListener('click', (e) => {
                    e.preventDefault();
                    optionCheckboxesMarca.forEach((checkbox: any) => {
                        checkbox.checked = true;
                    });
                });

                deselectAllButtonMarca.addEventListener('click', (e) => {
                    e.preventDefault();
                    optionCheckboxesMarca.forEach((checkbox: any) => {
                        checkbox.checked = false;
                    });
                });

                filterInputMarcas.addEventListener('input', (event: any) => {
                    const filterValue = String(event.target.value || '').toLowerCase();
                    const labels = marcasContainer.querySelectorAll('label');

                    labels.forEach((label: any) => {
                        const labelText = String(label.textContent || '').toLowerCase();
                        label.style.display = labelText.includes(filterValue) ? 'flex' : 'none';
                    });
                });
            },
            preConfirm: () => {
                const checkOptionsMarcaElement = document.getElementById('selectAllOptionsMarca') as HTMLInputElement;
                const checkOptionsMarca = checkOptionsMarcaElement.checked;

                const inputMaximoElement = document.getElementById('maximo') as HTMLInputElement;
                const inputMaximo = inputMaximoElement.value;

                // --- SIN VMD: leer + validar ---
                const includeSinVmdEl = document.getElementById('includeSinVmd') as HTMLInputElement | null;
                const cantidadSinVmdEl = document.getElementById('cantidadSinVmd') as HTMLInputElement | null;

                const includeSinVmd = includeSinVmdEl?.checked === true;

                let cantidadSinVmd = 0;
                if (includeSinVmd) {
                    cantidadSinVmd = parseInt(String(cantidadSinVmdEl?.value ?? ''), 10);
                    if (!Number.isFinite(cantidadSinVmd) || cantidadSinVmd < 1) {
                        Swal.showValidationMessage('La cantidad fija para productos sin VMD debe ser un entero mayor o igual a 1.');
                        return false;
                    }
                }

                // --- MARCAS ---
                const optionCheckboxesMarca = document.querySelectorAll('input[type="checkbox"][name="checkboxOptionMarca"]');
                const checkboxStatesMarca: any[] = [];

                optionCheckboxesMarca.forEach((checkbox: any) => {
                    checkboxStatesMarca.push({
                        name: checkbox.value,
                        checked: checkbox.checked
                    });
                });

                const selectedCount = checkboxStatesMarca.reduce((acc, x) => acc + (x?.checked === true ? 1 : 0), 0);
                if (selectedCount === 0) {
                    Swal.showValidationMessage('Debes seleccionar al menos una marca.');
                    return false;
                }

                const parsedInputMaximo = inputMaximo == "" ? 0 : parseFloat(inputMaximo);
                if (isNaN(parsedInputMaximo) || parsedInputMaximo < 0) {
                    Swal.showValidationMessage('Por favor, ingrese un valor válido mayor que 0 en los días.');
                    return false;
                }

                return {
                    inputMaximo: parsedInputMaximo,
                    checkOptionsMarca,
                    checkboxStatesMarca,
                    includeSinVmd,
                    cantidadSinVmd
                };
            },

        }).then(async (result: any) => {
            if (!result.isConfirmed) {
                return;
            }

            const payload = result.value;

            const selectedMarcas = getSelectedMarcas(payload);
            if (!Array.isArray(selectedMarcas) || selectedMarcas.length === 0) {
                await Swal.fire({
                    icon: 'warning',
                    title: 'Falta selección',
                    text: 'Debes seleccionar al menos una marca.'
                });
                return;
            }

            let force_overwrite = false;
            let transitoItemsToDelete: number[] = [];

            try {
                const token = localStorage.getItem("authToken");
                const headers = { Authorization: `Bearer ${token}` };

                const pre = await makeRequest<PrecheckResponse>(
                    `${apiRoute}/pedido/sugerido-precheck`,
                    { marcas: selectedMarcas },
                    { headers },
                    "POST"
                );

                if (pre?.data?.has_conflicts === true) {
                    const warn = await Swal.fire({
                        title: 'Confirmación requerida',
                        html: buildOverwriteWarningHtml(pre.data),
                        icon: 'warning',
                        width: 950,
                        showCancelButton: true,
                        confirmButtonText: 'Continuar',
                        cancelButtonText: 'Cancelar',
                        reverseButtons: true,
                        focusConfirm: false,
                        didOpen: () => {
                            const checkAll = document.getElementById('check_all_transitos') as HTMLInputElement | null;

                            if (checkAll) {
                                checkAll.addEventListener('change', () => {
                                    const checks = document.querySelectorAll('.check_transito_item') as NodeListOf<HTMLInputElement>;
                                    checks.forEach((el: HTMLInputElement) => {
                                        el.checked = checkAll.checked;
                                    });
                                });
                            }
                        },
                        preConfirm: () => {
                            const needsMarcaAck = pre?.data?.has_conflicts_marcas === true;
                            const needsTransitoAck = pre?.data?.has_conflicts_transito === true;

                            const ackMarcas = (document.getElementById('ack_overwrite_marcas') as HTMLInputElement | null)?.checked === true;
                            const ackTransito = (document.getElementById('ack_overwrite_transito') as HTMLInputElement | null)?.checked === true;

                            if (needsMarcaAck && !ackMarcas) {
                                Swal.showValidationMessage('Debes confirmar el conflicto de pedidos por marca para continuar.');
                                return false;
                            }

                            if (needsTransitoAck && !ackTransito) {
                                Swal.showValidationMessage('Debes confirmar el conflicto de productos en tránsito para continuar.');
                                return false;
                            }

                            const selectedItems = Array.from(
                                document.querySelectorAll('.check_transito_item:checked')
                            ).map((el: Element) => Number((el as HTMLInputElement).value))
                                .filter((id: number) => Number.isFinite(id) && id > 0);

                            return {
                                selectedItems,
                            };
                        }
                    });

                    if (!warn.isConfirmed) {
                        return;
                    }

                    transitoItemsToDelete = Array.isArray(warn.value?.selectedItems) ? warn.value.selectedItems : [];
                    force_overwrite = true;
                }
            } catch (e: any) {
                if (e?.response?.status === 422) {
                    const msg = e?.response?.data?.msg || e?.response?.data?.message || 'Debe seleccionar al menos una marca.';
                    await Swal.fire({ icon: 'warning', title: 'Validación', text: msg });
                    return;
                }

                await Swal.fire({
                    icon: 'error',
                    title: 'No se pudo validar',
                    text: 'No fue posible validar si existen pedidos activos o productos en tránsito. Intenta de nuevo.'
                });
                return;
            }

            if (transitoItemsToDelete.length > 0) {
                try {
                    const token = localStorage.getItem("authToken");
                    const headers = { Authorization: `Bearer ${token}` };

                    await makeRequest(
                        `${apiRoute}/pedido/transito-lab/marcar-recibidos`,
                        { items: transitoItemsToDelete },
                        { headers },
                        "POST"
                    );
                } catch (e: any) {
                    await Swal.fire({
                        icon: 'error',
                        title: 'No se pudo actualizar el tránsito',
                        text: 'Los productos seleccionados no pudieron eliminarse de tránsito.'
                    });
                    return;
                }
            }

            // 2) GENERAR
            const Toast = Swal.mixin({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timerProgressBar: true,
                didOpen: (toast) => {
                    toast.addEventListener('mouseenter', Swal.stopTimer);
                    toast.addEventListener('mouseleave', Swal.resumeTimer);
                }
            });

            Toast.showLoading();

            store.dispatch('orders/generateSuggested', {
                axios,
                apiRoute,
                data: {
                    ...payload,
                    force_overwrite
                },
            }).then(() => {
                const Toast2 = Swal.mixin({
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                    didOpen: (toast) => {
                        toast.addEventListener('mouseenter', Swal.stopTimer);
                        toast.addEventListener('mouseleave', Swal.resumeTimer);
                    }
                });

                Toast2.fire({
                    icon: 'success',
                    title: 'Se ha realizado la creación del pedido sugerido exitosamente.'
                });

                store.dispatch('pedidos/getPedidos', {
                    axios,
                    apiRoute,
                    showPedidos: true
                });
            }).catch(async (err: any) => {
                if (err?.response?.status === 409) {
                    await Swal.fire({
                        title: 'Conflicto detectado',
                        html: buildOverwriteWarningHtml(err.response.data),
                        icon: 'warning'
                    });
                    return;
                }

                if (err?.response?.status === 422) {
                    const msg = err?.response?.data?.msg || err?.response?.data?.message || 'Datos inválidos.';
                    await Swal.fire({ icon: 'warning', title: 'Validación', text: msg });
                    return;
                }

                await Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'No se pudo generar el pedido sugerido.'
                });
            });

        });
    });
}